import { safeStorage } from '../utils/safeLocalStorage';

/**
 * 子应用数据传递服务
 * 用于主应用向子应用传递数据的统一管理
 */
export class SubAppDataService {
  private static instance: SubAppDataService;
  
  static getInstance(): SubAppDataService {
    if (!SubAppDataService.instance) {
      SubAppDataService.instance = new SubAppDataService();
    }
    return SubAppDataService.instance;
  }

  /**
   * 保存子应用数据
   * @param appName 子应用名称
   * @param data 要传递的数据
   * @param ttlSeconds 过期时间（秒），默认30分钟
   */
  saveSubAppData(appName: string, data: SubAppData, ttlSeconds: number = 1800): void {
    const dataKey = this.generateDataKey(appName);
    const dataWithTimestamp = {
      ...data,
      timestamp: Date.now(),
      appName
    };
    
    safeStorage.set(dataKey, dataWithTimestamp, ttlSeconds);
    console.log(`✅ 已为子应用 ${appName} 保存数据`, { key: dataKey });
  }

  /**
   * 获取子应用数据
   * @param appName 子应用名称
   * @returns 子应用数据或null
   */
  getSubAppData(appName: string): SubAppDataWithMeta | null {
    const dataKey = this.generateDataKey(appName);
    const data = safeStorage.get<SubAppDataWithMeta>(dataKey);
    
    if (data) {
      console.log(`✅ 成功获取子应用 ${appName} 数据`);
      return data;
    } else {
      console.warn(`⚠️ 未找到子应用 ${appName} 的数据`);
      return null;
    }
  }

  /**
   * 清理子应用数据
   * @param appName 子应用名称
   */
  clearSubAppData(appName: string): void {
    const dataKey = this.generateDataKey(appName);
    safeStorage.remove(dataKey);
    console.log(`🗑️ 已清理子应用 ${appName} 数据`);
  }

  /**
   * 保存用户中心特殊数据（仅针对userManage应用）
   * @param appName 子应用名称
   * @param userCenterData 用户中心数据
   * @param ttlSeconds 过期时间（秒）
   */
  saveUserCenterData(appName: string, userCenterData: any, ttlSeconds: number = 1800): void {
    const userCenterKey = this.generateUserCenterKey(appName);
    safeStorage.set(userCenterKey, {
      userCenterMenu: userCenterData,
      timestamp: Date.now()
    }, ttlSeconds);
    console.log(`✅ 已为子应用 ${appName} 保存用户中心数据`);
  }

  /**
   * 获取用户中心数据
   * @param appName 子应用名称
   * @returns 用户中心数据或null
   */
  getUserCenterData(appName: string): any {
    const userCenterKey = this.generateUserCenterKey(appName);
    const data = safeStorage.get<any>(userCenterKey);
    return data?.userCenterMenu || null;
  }

  /**
   * 生成子应用数据存储key
   * @param appName 子应用名称
   * @returns 存储key
   */
  private generateDataKey(appName: string): string {
    return `subapp_data_${appName}`;
  }

  /**
   * 生成用户中心数据存储key
   * @param appName 子应用名称
   * @returns 存储key
   */
  private generateUserCenterKey(appName: string): string {
    return `subapp_usercenter_${appName}`;
  }

  /**
   * 获取所有子应用数据的key列表
   * @returns key列表
   */
  getAllSubAppKeys(): string[] {
    return safeStorage.getAllKeys().filter(key => 
      key.startsWith('subapp_data_') || key.startsWith('subapp_usercenter_')
    );
  }

  /**
   * 清理所有子应用数据
   */
  clearAllSubAppData(): void {
    const keys = this.getAllSubAppKeys();
    keys.forEach(key => {
      safeStorage.remove(key);
    });
    console.log(`🗑️ 已清理所有子应用数据，共 ${keys.length} 个`);
  }
}

/**
 * 子应用数据接口
 */
export interface SubAppData {
  userId: string;
  menuData: string; // JSON字符串
  authList: string; // JSON字符串
}

/**
 * 带元数据的子应用数据接口
 */
export interface SubAppDataWithMeta extends SubAppData {
  timestamp: number;
  appName: string;
}

// 导出单例实例
export const subAppDataService = SubAppDataService.getInstance();